import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Send, Plus, Trash2, Eye, Mail, Users, Zap, History } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  listMarketingAudience,
  listMarketingTemplates,
  upsertMarketingTemplate,
  deleteMarketingTemplate,
  sendMarketingCampaign,
  listMarketingCampaigns,
  listMarketingAutomations,
  upsertMarketingAutomation,
  deleteMarketingAutomation,
} from "@/utils/marketing.functions";

export const Route = createFileRoute("/_app/admin/marketing")({
  component: MarketingPage,
});

type Template = {
  id: string;
  slug: string;
  name: string;
  subject: string;
  preview_text: string | null;
  body_html: string;
  language: string;
};

type AudienceRow = {
  email: string;
  name: string | null;
  source: string;
  language: string;
  last_activity_at: string;
  metadata: Record<string, unknown>;
};

type Campaign = {
  id: string;
  template_slug: string | null;
  audience_source: string | null;
  recipient_count: number;
  status: string;
  created_at: string;
  marketing_email_templates: { name: string; slug: string } | null;
};

type Automation = {
  id: string;
  name: string;
  trigger_key: string;
  template_id: string;
  enabled: boolean;
  cooldown_days: number;
  last_run_at: string | null;
  marketing_email_templates: { name: string } | null;
};

const SOURCE_LABEL_KEY: Record<string, string> = {
  signup: "pages.adminMarketing.sources.signup",
  lead: "pages.adminMarketing.sources.lead",
  inactive_company: "pages.adminMarketing.sources.inactiveCompany",
  invite_pending: "pages.adminMarketing.sources.invitePending",
  history: "pages.adminMarketing.sources.history",
};

const TRIGGER_LABEL_KEY: Record<string, string> = {
  lead_no_signup_3d: "pages.adminMarketing.triggers.leadNoSignup3d",
  signup_inactive_7d: "pages.adminMarketing.triggers.signupInactive7d",
  client_invite_pending_2d: "pages.adminMarketing.triggers.clientInvitePending2d",
  history_reengage_30d: "pages.adminMarketing.triggers.historyReengage30d",
};

function MarketingPage() {
  const { t } = useTranslation();
  const { hasRole, loading } = useAuth();
  if (loading) return <div className="p-6">{t("pages.adminMarketing.loading")}</div>;
  if (!hasRole("owner")) return <Navigate to="/dashboard" />;

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t("pages.adminMarketing.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("pages.adminMarketing.subtitle")}</p>
      </div>
      <Tabs defaultValue="audience" className="space-y-4">
        <TabsList>
          <TabsTrigger value="audience">
            <Users className="mr-2 h-4 w-4" /> {t("pages.adminMarketing.tabs.audience")}
          </TabsTrigger>
          <TabsTrigger value="templates">
            <Mail className="mr-2 h-4 w-4" /> {t("pages.adminMarketing.tabs.templates")}
          </TabsTrigger>
          <TabsTrigger value="campaigns">
            <History className="mr-2 h-4 w-4" /> {t("pages.adminMarketing.tabs.campaigns")}
          </TabsTrigger>
          <TabsTrigger value="automations">
            <Zap className="mr-2 h-4 w-4" /> {t("pages.adminMarketing.tabs.automations")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="audience">
          <AudienceTab />
        </TabsContent>
        <TabsContent value="templates">
          <TemplatesTab />
        </TabsContent>
        <TabsContent value="campaigns">
          <CampaignsTab />
        </TabsContent>
        <TabsContent value="automations">
          <AutomationsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ───────── AUDIENCE ───────── */
function AudienceTab() {
  const { t } = useTranslation();
  const listAudienceFn = useServerFn(listMarketingAudience);
  const listTemplatesFn = useServerFn(listMarketingTemplates);
  const sendFn = useServerFn(sendMarketingCampaign);

  const [source, setSource] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<AudienceRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [sendDialog, setSendDialog] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listAudienceFn({
        data: { source: source as any, search: search.trim() || undefined, limit: 1000 },
      });
      setRows(res.rows as AudienceRow[]);
      setSelected(new Set());
    } catch (e) {
      toast.error(t("pages.adminMarketing.audience.toasts.loadError"), {
        description: (e as Error).message,
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  useEffect(() => {
    listTemplatesFn().then((r) => setTemplates(r.templates as Template[]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.email)));
  };

  const handleSend = async () => {
    if (!templateId) return toast.error(t("pages.adminMarketing.audience.toasts.chooseTemplate"));
    const recipients = rows
      .filter((r) => selected.has(r.email))
      .map((r) => ({ email: r.email, name: r.name, language: r.language }));
    if (recipients.length === 0)
      return toast.error(t("pages.adminMarketing.audience.toasts.noRecipients"));
    setSending(true);
    try {
      const res = await sendFn({
        data: { templateId, recipients, audienceSource: source },
      });
      toast.success(
        t("pages.adminMarketing.audience.toasts.queued", { sent: res.sent, failed: res.failed }),
      );
      setSendDialog(false);
      setSelected(new Set());
    } catch (e) {
      toast.error(t("pages.adminMarketing.audience.toasts.sendError"), {
        description: (e as Error).message,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label>{t("pages.adminMarketing.audience.label")}</Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("pages.adminMarketing.audience.options.all")}
                </SelectItem>
                <SelectItem value="signup">
                  {t("pages.adminMarketing.audience.options.signup")}
                </SelectItem>
                <SelectItem value="lead">
                  {t("pages.adminMarketing.audience.options.lead")}
                </SelectItem>
                <SelectItem value="inactive_company">
                  {t("pages.adminMarketing.audience.options.inactiveCompany")}
                </SelectItem>
                <SelectItem value="invite_pending">
                  {t("pages.adminMarketing.audience.options.invitePending")}
                </SelectItem>
                <SelectItem value="history">
                  {t("pages.adminMarketing.audience.options.history")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 flex-1 min-w-[200px]">
            <Label>{t("pages.adminMarketing.audience.searchLabel")}</Label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder={t("pages.adminMarketing.audience.searchPlaceholder")}
            />
          </div>
          <Button variant="outline" onClick={load} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t("pages.adminMarketing.audience.refresh")
            )}
          </Button>
          <Button onClick={() => setSendDialog(true)} disabled={selected.size === 0}>
            <Send className="mr-2 h-4 w-4" />{" "}
            {t("pages.adminMarketing.audience.sendTo", { n: selected.size })}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-sm text-muted-foreground mb-2">
          {t("pages.adminMarketing.audience.contactsCount", { n: rows.length })}
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={rows.length > 0 && selected.size === rows.length}
                    onCheckedChange={toggleAll}
                  />
                </TableHead>
                <TableHead>{t("pages.adminMarketing.audience.table.email")}</TableHead>
                <TableHead>{t("pages.adminMarketing.audience.table.name")}</TableHead>
                <TableHead>{t("pages.adminMarketing.audience.table.source")}</TableHead>
                <TableHead>{t("pages.adminMarketing.audience.table.lastActivity")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.email}>
                  <TableCell>
                    <Checkbox
                      checked={selected.has(r.email)}
                      onCheckedChange={(c) => {
                        const next = new Set(selected);
                        if (c) next.add(r.email);
                        else next.delete(r.email);
                        setSelected(next);
                      }}
                    />
                  </TableCell>
                  <TableCell className="font-mono text-xs">{r.email}</TableCell>
                  <TableCell>{r.name || "—"}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {SOURCE_LABEL_KEY[r.source] ? t(SOURCE_LABEL_KEY[r.source]) : r.source}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {r.last_activity_at
                      ? new Date(r.last_activity_at).toLocaleDateString("pt-BR")
                      : "—"}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    {t("pages.adminMarketing.audience.empty")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <Dialog open={sendDialog} onOpenChange={setSendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("pages.adminMarketing.audience.dialog.title", { n: selected.size })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label>{t("pages.adminMarketing.audience.dialog.templateLabel")}</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger>
                <SelectValue
                  placeholder={t("pages.adminMarketing.audience.dialog.templatePlaceholder")}
                />
              </SelectTrigger>
              <SelectContent>
                {templates.map((tpl) => (
                  <SelectItem key={tpl.id} value={tpl.id}>
                    {tpl.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialog(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSend} disabled={sending}>
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t("pages.adminMarketing.audience.dialog.send")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ───────── TEMPLATES ───────── */
function TemplatesTab() {
  const { t } = useTranslation();
  const listFn = useServerFn(listMarketingTemplates);
  const upsertFn = useServerFn(upsertMarketingTemplate);
  const deleteFn = useServerFn(deleteMarketingTemplate);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [editing, setEditing] = useState<Template | null>(null);
  const [preview, setPreview] = useState<Template | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listFn();
      setTemplates(r.templates as Template[]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const newTemplate: Template = {
    id: "",
    slug: "",
    name: "",
    subject: "",
    preview_text: "",
    body_html: "<p>Olá {{name}},</p>",
    language: "pt",
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t("pages.adminMarketing.templates.title", { n: templates.length })}</CardTitle>
        <Button onClick={() => setEditing(newTemplate)}>
          <Plus className="mr-2 h-4 w-4" /> {t("pages.adminMarketing.templates.new")}
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 md:grid-cols-2">
          {templates.map((tpl) => (
            <div
              key={tpl.id}
              className="rounded-lg border p-4 space-y-2 hover:bg-muted/30 transition"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-medium">{tpl.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{tpl.slug}</div>
                </div>
                <Badge variant="outline">{tpl.language.toUpperCase()}</Badge>
              </div>
              <div className="text-sm text-muted-foreground line-clamp-1">{tpl.subject}</div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" variant="outline" onClick={() => setPreview(tpl)}>
                  <Eye className="h-3 w-3 mr-1" /> {t("pages.adminMarketing.templates.preview")}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(tpl)}>
                  {t("common.edit")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive ml-auto"
                  onClick={async () => {
                    if (
                      !confirm(
                        t("pages.adminMarketing.templates.deleteConfirm", { name: tpl.name }),
                      )
                    )
                      return;
                    await deleteFn({ data: { id: tpl.id } });
                    load();
                  }}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
          {templates.length === 0 && !loading && (
            <div className="col-span-2 text-center text-muted-foreground py-8">
              {t("pages.adminMarketing.templates.empty")}
            </div>
          )}
        </div>
      </CardContent>

      {editing && (
        <TemplateEditorDialog
          template={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
          upsertFn={upsertFn as any}
        />
      )}

      {preview && (
        <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {t("pages.adminMarketing.templates.previewDialog.title", { name: preview.name })}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-2">
              <div className="text-sm">
                <strong>{t("pages.adminMarketing.templates.previewDialog.subject")}</strong>{" "}
                {preview.subject}
              </div>
              {preview.preview_text && (
                <div className="text-xs text-muted-foreground">
                  <strong>{t("pages.adminMarketing.templates.previewDialog.previewText")}</strong>{" "}
                  {preview.preview_text}
                </div>
              )}
              <div
                className="border rounded p-4 bg-white text-black max-h-[60vh] overflow-auto"
                dangerouslySetInnerHTML={{ __html: preview.body_html }}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}

function TemplateEditorDialog({
  template,
  onClose,
  onSaved,
  upsertFn,
}: {
  template: Template;
  onClose: () => void;
  onSaved: () => void;
  upsertFn: (args: { data: any }) => Promise<{ id: string }>;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(template);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await upsertFn({
        data: {
          id: form.id || undefined,
          slug: form.slug,
          name: form.name,
          subject: form.subject,
          preview_text: form.preview_text || undefined,
          body_html: form.body_html,
          language: form.language,
        },
      });
      toast.success(t("pages.adminMarketing.templates.editor.toasts.saved"));
      onSaved();
    } catch (e) {
      toast.error(t("pages.adminMarketing.templates.editor.toasts.saveError"), {
        description: (e as Error).message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {form.id
              ? t("pages.adminMarketing.templates.editor.titleEdit")
              : t("pages.adminMarketing.templates.editor.titleNew")}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label>{t("pages.adminMarketing.templates.editor.name")}</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label>{t("pages.adminMarketing.templates.editor.slug")}</Label>
            <Input
              value={form.slug}
              onChange={(e) =>
                setForm({ ...form, slug: e.target.value.replace(/[^a-z0-9_]/g, "_") })
              }
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label>
              {t("pages.adminMarketing.templates.editor.subjectLabel", { code: "{{name}}" })}
            </Label>
            <Input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label>{t("pages.adminMarketing.templates.editor.previewTextLabel")}</Label>
            <Input
              value={form.preview_text || ""}
              onChange={(e) => setForm({ ...form, preview_text: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label>{t("pages.adminMarketing.templates.editor.languageLabel")}</Label>
            <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pt">
                  {t("pages.adminMarketing.templates.editor.languages.pt")}
                </SelectItem>
                <SelectItem value="en">
                  {t("pages.adminMarketing.templates.editor.languages.en")}
                </SelectItem>
                <SelectItem value="es">
                  {t("pages.adminMarketing.templates.editor.languages.es")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-2 mt-3">
          <div className="space-y-1">
            <Label>{t("pages.adminMarketing.templates.editor.bodyHtml")}</Label>
            <Textarea
              className="font-mono text-xs min-h-[300px]"
              value={form.body_html}
              onChange={(e) => setForm({ ...form, body_html: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label>{t("pages.adminMarketing.templates.editor.livePreview")}</Label>
            <div
              className="border rounded p-3 bg-white text-black min-h-[300px] overflow-auto text-sm"
              dangerouslySetInnerHTML={{
                __html: form.body_html.replace(/\{\{name\}\}/g, "João"),
              }}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───────── CAMPAIGNS ───────── */
function CampaignsTab() {
  const { t } = useTranslation();
  const listFn = useServerFn(listMarketingCampaigns);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  useEffect(() => {
    listFn().then((r) => setCampaigns(r.campaigns as Campaign[]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("pages.adminMarketing.campaigns.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("pages.adminMarketing.campaigns.table.date")}</TableHead>
              <TableHead>{t("pages.adminMarketing.campaigns.table.template")}</TableHead>
              <TableHead>{t("pages.adminMarketing.campaigns.table.audience")}</TableHead>
              <TableHead className="text-right">
                {t("pages.adminMarketing.campaigns.table.recipients")}
              </TableHead>
              <TableHead>{t("pages.adminMarketing.campaigns.table.status")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {campaigns.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="text-xs">
                  {new Date(c.created_at).toLocaleString("pt-BR")}
                </TableCell>
                <TableCell>{c.marketing_email_templates?.name || c.template_slug || "—"}</TableCell>
                <TableCell>
                  {SOURCE_LABEL_KEY[c.audience_source || ""]
                    ? t(SOURCE_LABEL_KEY[c.audience_source || ""])
                    : c.audience_source}
                </TableCell>
                <TableCell className="text-right">{c.recipient_count}</TableCell>
                <TableCell>
                  <Badge
                    variant={
                      c.status === "sent"
                        ? "default"
                        : c.status === "failed"
                          ? "destructive"
                          : "secondary"
                    }
                  >
                    {c.status}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
            {campaigns.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  {t("pages.adminMarketing.campaigns.empty")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

/* ───────── AUTOMATIONS ───────── */
function AutomationsTab() {
  const { t } = useTranslation();
  const listFn = useServerFn(listMarketingAutomations);
  const upsertFn = useServerFn(upsertMarketingAutomation);
  const deleteFn = useServerFn(deleteMarketingAutomation);
  const listTplFn = useServerFn(listMarketingTemplates);
  const [items, setItems] = useState<Automation[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [editing, setEditing] = useState<Partial<Automation> | null>(null);

  const load = async () => {
    const r = await listFn();
    setItems(r.automations as Automation[]);
  };
  useEffect(() => {
    load();
    listTplFn().then((r) => setTemplates(r.templates as Template[]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (!editing) return;
    if (!editing.name || !editing.trigger_key || !editing.template_id) {
      return toast.error(t("pages.adminMarketing.automations.toasts.fillAllFields"));
    }
    try {
      await upsertFn({
        data: {
          id: editing.id,
          name: editing.name,
          trigger_key: editing.trigger_key as any,
          template_id: editing.template_id,
          enabled: editing.enabled ?? false,
          cooldown_days: editing.cooldown_days ?? 14,
        },
      });
      toast.success(t("pages.adminMarketing.automations.toasts.saved"));
      setEditing(null);
      load();
    } catch (e) {
      toast.error(t("pages.adminMarketing.automations.toasts.error"), {
        description: (e as Error).message,
      });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t("pages.adminMarketing.automations.title", { n: items.length })}</CardTitle>
        <Button onClick={() => setEditing({ name: "", cooldown_days: 14, enabled: false })}>
          <Plus className="mr-2 h-4 w-4" /> {t("pages.adminMarketing.automations.new")}
        </Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("pages.adminMarketing.automations.table.name")}</TableHead>
              <TableHead>{t("pages.adminMarketing.automations.table.trigger")}</TableHead>
              <TableHead>{t("pages.adminMarketing.automations.table.template")}</TableHead>
              <TableHead>{t("pages.adminMarketing.automations.table.cooldown")}</TableHead>
              <TableHead>{t("pages.adminMarketing.automations.table.lastRun")}</TableHead>
              <TableHead>{t("pages.adminMarketing.automations.table.active")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((a) => (
              <TableRow key={a.id}>
                <TableCell className="font-medium">{a.name}</TableCell>
                <TableCell className="text-xs">
                  {TRIGGER_LABEL_KEY[a.trigger_key]
                    ? t(TRIGGER_LABEL_KEY[a.trigger_key])
                    : a.trigger_key}
                </TableCell>
                <TableCell>{a.marketing_email_templates?.name || "—"}</TableCell>
                <TableCell>
                  {t("pages.adminMarketing.automations.cooldownValue", { days: a.cooldown_days })}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {a.last_run_at
                    ? new Date(a.last_run_at).toLocaleString("pt-BR")
                    : t("pages.adminMarketing.automations.never")}
                </TableCell>
                <TableCell>
                  <Switch
                    checked={a.enabled}
                    onCheckedChange={async (v) => {
                      await upsertFn({
                        data: {
                          id: a.id,
                          name: a.name,
                          trigger_key: a.trigger_key as any,
                          template_id: a.template_id,
                          enabled: v,
                          cooldown_days: a.cooldown_days,
                        },
                      });
                      load();
                    }}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(a)}>
                      {t("common.edit")}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive"
                      onClick={async () => {
                        if (!confirm(t("pages.adminMarketing.automations.deleteConfirm"))) return;
                        await deleteFn({ data: { id: a.id } });
                        load();
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  {t("pages.adminMarketing.automations.empty")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>

      {editing && (
        <Dialog open onOpenChange={() => setEditing(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editing.id
                  ? t("pages.adminMarketing.automations.editor.titleEdit")
                  : t("pages.adminMarketing.automations.editor.titleNew")}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>{t("pages.adminMarketing.automations.editor.name")}</Label>
                <Input
                  value={editing.name || ""}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>{t("pages.adminMarketing.automations.editor.triggerLabel")}</Label>
                <Select
                  value={editing.trigger_key || ""}
                  onValueChange={(v) => setEditing({ ...editing, trigger_key: v })}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("pages.adminMarketing.automations.editor.triggerPlaceholder")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TRIGGER_LABEL_KEY).map(([k, labelKey]) => (
                      <SelectItem key={k} value={k}>
                        {t(labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>{t("pages.adminMarketing.automations.editor.templateLabel")}</Label>
                <Select
                  value={editing.template_id || ""}
                  onValueChange={(v) => setEditing({ ...editing, template_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("pages.adminMarketing.automations.editor.templatePlaceholder")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>{t("pages.adminMarketing.automations.editor.cooldownLabel")}</Label>
                <Input
                  type="number"
                  min={1}
                  value={editing.cooldown_days ?? 14}
                  onChange={(e) =>
                    setEditing({ ...editing, cooldown_days: Number(e.target.value) })
                  }
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={editing.enabled ?? false}
                  onCheckedChange={(v) => setEditing({ ...editing, enabled: v })}
                />
                <Label>{t("pages.adminMarketing.automations.editor.active")}</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>
                {t("common.cancel")}
              </Button>
              <Button onClick={save}>{t("common.save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}
